import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Briefcase,
  Building2,
  MapPin,
  Clock,
  ArrowLeft,
  Sparkles,
  Search,
  CheckCircle
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ApplyModal = ({ job, isOpen, onClose }) => {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", resume: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const jobSummary = `Role: ${job.job_title}\nCompany: ${job.company_name}${job.location ? `\nLocation: ${job.location}` : ""}${job.experience ? `\nExperience: ${job.experience}` : ""}`;
      const fullMessage = `APPLICATION for ${job.job_title} (${job.company_name})\n\n${jobSummary}\n\nResume / LinkedIn: ${formData.resume || "Not provided"}\n\nMessage from candidate:\n${formData.message || "(none)"}`;

      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: fullMessage
      });

      setSuccess(true);
      setFormData({ name: "", email: "", phone: "", resume: "", message: "" });
      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 2500);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  if (!job) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white mb-3">
            <Briefcase className="h-7 w-7" />
          </div>
          <DialogTitle className="text-2xl">Apply for {job.job_title}</DialogTitle>
          <DialogDescription>
            <span className="font-semibold text-orange-600">{job.company_name}</span>
            {job.location ? ` · ${job.location}` : ""}
            {job.experience ? ` · ${job.experience}` : ""}
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="text-center py-8" data-testid="apply-success">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900">Application Received!</h3>
            <p className="text-gray-600 mt-2">Our team will review your profile and get back within 24 hours.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 mt-2" data-testid="apply-form">
            {error && <div className="p-3 bg-red-50 text-red-600 rounded-lg text-sm">{error}</div>}

            <div className="space-y-2">
              <Label htmlFor="apply-name">Full Name *</Label>
              <Input id="apply-name" placeholder="Enter your full name" value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="apply-name" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="apply-email">Email *</Label>
                <Input id="apply-email" type="email" placeholder="you@email.com" value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })} required data-testid="apply-email" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="apply-phone">Phone *</Label>
                <Input id="apply-phone" placeholder="+91 9XXXXXXXXX" value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required data-testid="apply-phone" />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="apply-resume">Resume / LinkedIn / Google Drive link</Label>
              <Input id="apply-resume" placeholder="https://..." value={formData.resume}
                onChange={(e) => setFormData({ ...formData, resume: e.target.value })} data-testid="apply-resume" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="apply-message">Cover message (optional)</Label>
              <Textarea id="apply-message" placeholder="Tell us about your experience relevant to this role"
                value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                rows={3} data-testid="apply-message" />
            </div>

            <Button type="submit" disabled={loading} data-testid="apply-submit"
              className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:opacity-90 text-white py-6 text-lg rounded-full">
              {loading ? "Submitting..." : "Submit Application"}
            </Button>

            <p className="text-xs text-gray-500 text-center">
              We&apos;ll contact shortlisted candidates within 24 hours.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

const JobsPage = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedJob, setSelectedJob] = useState(null);
  const [isApplyOpen, setIsApplyOpen] = useState(false);
  const navigate = useNavigate();

  const openApply = (job) => {
    setSelectedJob(job);
    setIsApplyOpen(true);
  };

  const closeApply = () => {
    setIsApplyOpen(false);
    setSelectedJob(null);
  };

  useEffect(() => {
    loadJobs();
  }, []);

  const loadJobs = async () => {
    try {
      const res = await axios.get(`${API}/jobs`);
      setJobs(res.data.jobs);
    } catch (err) {
      console.error("Error loading jobs:", err);
    }
    setLoading(false);
  };

  const filteredJobs = jobs.filter(job => 
    job.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    job.job_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    job.job_description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate("/")}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-orange-500" />
            <span className="text-xl font-bold text-gray-900">Current Openings</span>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold mb-4">Find Your Dream Job</h1>
          <p className="text-orange-100 mb-8 max-w-2xl mx-auto">
            Browse through our latest job openings from top companies. Apply now and take the next step in your career!
          </p>
          
          {/* Search Bar */}
          <div className="max-w-xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by company, job title, or keywords..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-4 rounded-full text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-300"
            />
          </div>
        </div>
      </div>

      {/* Jobs List */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        {loading ? (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading jobs...</p>
          </div>
        ) : filteredJobs.length === 0 ? (
          <div className="text-center py-16">
            <Briefcase className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {searchTerm ? "No jobs match your search" : "No job openings yet"}
            </h3>
            <p className="text-gray-600">
              {searchTerm ? "Try different keywords" : "Check back soon for new opportunities!"}
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            <p className="text-gray-600 mb-4">{filteredJobs.length} job(s) found</p>
            
            {filteredJobs.map((job) => (
              <Card 
                key={job.id} 
                className="hover:shadow-lg transition-shadow border-l-4 border-l-orange-500"
              >
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-1">
                        {job.job_title}
                      </h3>
                      <div className="flex items-center gap-2 text-orange-600 font-medium">
                        <Building2 className="h-4 w-4" />
                        {job.company_name}
                      </div>
                    </div>
                    <span className="text-sm text-gray-500 flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {formatDate(job.created_at)}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-4 mb-4 text-sm text-gray-600">
                    {job.location && (
                      <span className="flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        {job.location}
                      </span>
                    )}
                    {job.experience && (
                      <span className="flex items-center gap-1">
                        <Briefcase className="h-4 w-4" />
                        {job.experience}
                      </span>
                    )}
                  </div>

                  <div className="prose prose-sm max-w-none">
                    <p className="text-gray-700 whitespace-pre-line">
                      {job.job_description}
                    </p>
                  </div>

                  <div className="mt-6 pt-4 border-t">
                    <Button 
                      onClick={() => openApply(job)}
                      className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-6"
                      data-testid={`apply-btn-${job.id}`}
                    >
                      Apply Now
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* CTA Section */}
      <div className="bg-slate-900 text-white py-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold mb-4">Looking to hire?</h2>
          <p className="text-gray-400 mb-6">
            Post your job requirements and find the perfect candidates through MC Talent Solutions
          </p>
          <Button 
            onClick={() => navigate("/")}
            className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-8"
          >
            Post a Job
          </Button>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-800 text-gray-400 py-6 text-center text-sm">
        <p>© 2026 MC Talent Solutions Pvt. Ltd. All rights reserved.</p>
      </footer>

      <ApplyModal job={selectedJob} isOpen={isApplyOpen} onClose={closeApply} />
    </div>
  );
};

export default JobsPage;
